import type {StoichiometryStructuredTableRowMetadata} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {
  StructuredTableColumnGroups,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import * as Util from 'notebook/tables/excess/Util';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';

/**
 * Reads the resolved IUPAC name for a row (if the IUPAC Name column is present and resolved). This
 * is used to pre-fill the Create Molecule modal name field for unrecognized molecules. Returns
 * undefined when the column is absent or the cell has no resolved text value.
 *
 * The IUPAC column resolves to a STRING typed value, whose text lives in `rawValue` (not a
 * FieldType.TEXT `value`).
 */
function getResolvedIupacName(row: Array<EvaluatedCell>, iupacColumnIndex: number): string | undefined {
  if (iupacColumnIndex < 0) {
    return undefined;
  }
  const iupacValue = row[iupacColumnIndex]?.value;
  if (iupacValue != null && iupacValue.type === BaseTypedValueType.STRING && iupacValue.rawValue !== '') {
    return iupacValue.rawValue;
  }
  return undefined;
}

export default function attachUnrecognizedMoleculeResolutionActions(
  computedTable: Array<Array<EvaluatedCell>> | null | undefined,
  structuredTable: UnknownStructuredTable,
  getResolutionAction?: (canonicalSmiles: string, initialName?: string) => () => void
): void {
  if (!getResolutionAction || !computedTable) {
    return;
  }
  // The IUPAC Name column index is table-level metadata, so compute it once rather than per row.
  const iupacColumnIndex = structuredTable.columns.findIndex(
    (column) => column.columnGroup === StructuredTableColumnGroups.CHEMISTRY_IUPAC_NAME
  );
  for (let rowIndex = 0; rowIndex < computedTable.length; rowIndex++) {
    const row = computedTable[rowIndex];
    const rowMetadata = structuredTable.rows[rowIndex]?.metadata as
      | StoichiometryStructuredTableRowMetadata
      | undefined;
    const canonicalSmiles = rowMetadata?.canonicalSmiles;
    if (!canonicalSmiles) {
      continue;
    }
    const iupacName = getResolvedIupacName(row, iupacColumnIndex);
    for (const cell of row) {
      if (cell.error) {
        const errors = Array.isArray(cell.error) ? cell.error : [cell.error];
        for (const error of errors) {
          if (Util.isUnrecognizedMoleculeWarning(error) && !error.resolutionAction) {
            error.resolutionAction = getResolutionAction(canonicalSmiles, iupacName);
            error.resolutionActionLabel = 'Create molecule';
          }
        }
      }
    }
  }
}
